import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

import { BrandMark } from './Brand';
import { NavIcon } from './NavIcon';
import { PageTitleProvider } from './PageTitle';
import { ThemeToggle } from './ThemeToggle';
import { focusableIn } from './primitives';
import { navLabelFor, type NavSection } from '../nav';

/**
 * The application shell: a left sidebar, a slim topbar, and the page.
 *
 * ## Why the navigation is on the left and only behind a session
 *
 * It sits beside Forge and Amplify, which are the same shape, and a creator or
 * operator who uses two of the three should not have to relearn where the app
 * is. A column also scales where a bar does not: an operator has nine
 * destinations in three groups, and nine tabs across the top of a laptop is a
 * row nobody can scan.
 *
 * Signed OUT there is no sidebar at all (`PublicShell`), because every one of
 * these links leads somewhere that would bounce a visitor back to sign in. An
 * app's tabs are not an advertisement.
 *
 * ## One navigation, one person
 *
 * A creator sees the creator sections and an operator sees the operator
 * sections — never both, and there is no switcher, because an account that is
 * both does not exist in v1. Hiding the operator's links from a creator is a
 * COURTESY, NOT A CONTROL: the routes are in this public bundle and anybody can
 * type the address. What refuses them is the API's 403.
 *
 * ## The drawer
 *
 * Under 900px the sidebar leaves the grid and becomes an off-canvas drawer the
 * topbar's button opens. Every dismissal — Escape, the backdrop, a tap on a
 * link, a route change — goes through one function so focus returns to the
 * button in every case and not just the one that was easy to write. While it
 * is open, Tab is held inside it, the page behind the backdrop cannot scroll,
 * and opening it moves focus onto the first link — the same three rules
 * Forge's drawer keeps, because a menu that lets the keyboard walk out from
 * under it, or lets a touch-drag scroll the page behind it, is not closed —
 * it only looks closed.
 */
export function AppShell({
  sections,
  user,
  onSignOut,
  homePath,
  badges,
  children,
}: {
  sections: NavSection[];
  user: { name: string; email: string; role: string };
  onSignOut: () => void;
  /** Where the brand lockup leads: this person's own first page. */
  homePath: string;
  /**
   * A count beside a nav item, keyed on its path. Zero or absent draws
   * nothing: a badge that says 0 is a badge that has taught people to ignore
   * it. The number is part of the link's accessible name ("Support, 3
   * unread"), so a screen reader hears it where a sighted person sees it.
   */
  badges?: Record<string, number>;
  children: React.ReactNode;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [pageTitle, setPageTitle] = useState<string | null>(null);
  const { pathname } = useLocation();
  const toggleRef = useRef<HTMLButtonElement>(null);
  const sidebarRef = useRef<HTMLElement>(null);

  /* The ONE close path. A drawer that only restores focus when it was Escape
     that closed it leaves a keyboard on a link translated off the screen. */
  const close = useCallback(() => {
    setOpen((wasOpen) => {
      if (wasOpen) toggleRef.current?.focus();
      return false;
    });
  }, []);

  /* A tap on a link inside the drawer navigates; leaving the drawer open over
     the page it just moved to is how a phone menu traps somebody. */
  useEffect(() => {
    close();
  }, [pathname, close]);

  /* A backdrop over the page is a promise that the page behind it is out of
     reach — a promise a scrollable `<body>` breaks on the first touch-drag,
     which scrolls the content under a drawer that looks closed to the touch
     doing it. Locked for exactly as long as the drawer is open, and restored
     to whatever it was rather than assumed to be empty, so a page that had
     its own reason to set `overflow` gets it back. */
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  /* While open: Escape closes, and Tab is held inside the drawer. Nothing
     else does that on its own — Tab walks straight out of an untrapped
     drawer and into the page underneath the backdrop, which is a keyboard
     trap of the worse kind: nothing on screen shows where focus went.
     Opening also moves focus onto the first link, the way a menu that just
     appeared should be entered, not left sitting on the button that opened
     it. */
  useEffect(() => {
    if (!open) return;
    const aside = sidebarRef.current;
    if (!aside) return;

    focusableIn(aside)[0]?.focus();

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        close();
        return;
      }
      if (event.key !== 'Tab') return;
      const stops = focusableIn(aside);
      if (stops.length === 0) return;
      const first = stops[0]!;
      const last = stops[stops.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  /* Rotate a phone to landscape with the drawer open and the sidebar goes back
     to being a column — but its full-viewport backdrop would stay mounted over
     everything, eating every click until a reload. Close it the moment the
     viewport is wide enough that the drawer is not the layout any more. Not
     `close()`: at that width the button is display:none, so there is nothing
     to hand focus back to. */
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const wide = window.matchMedia('(min-width: 900px)');
    const onChange = (event: MediaQueryListEvent): void => {
      if (event.matches) setOpen(false);
    };
    wide.addEventListener('change', onChange);
    return () => wide.removeEventListener('change', onChange);
  }, []);

  /* The topbar always says something, and it is never just the `<h1>` said
     twice. A page's own heading already sits a few pixels below it — for a
     campaign detail page that heading is the campaign's own name ("Jag —
     10M views"), not the word "Campaigns" — so a topbar that also printed
     the heading would repeat the same words in the same breath. The nav
     label ("Campaigns") is what orients someone in the sidebar's terms
     instead: it is what is lit up on the left, and it is stable across every
     page under that section. Only when there is no nav entry for this
     address at all (the front door, a 404, sign-in) does the topbar fall
     back to the page's own title, then to the product name. */
  const title = navLabelFor(pathname) ?? pageTitle ?? 'Nexus';

  return (
    <div className="app-shell">
      {/* First stop for a keyboard, last thing a mouse ever sees. Ten
          navigation links before the content is ten tab stops on every page
          without it. */}
      <a className="skip-link" href="#main">
        Skip to content
      </a>

      {open && <div className="drawer-backdrop" onClick={close} aria-hidden="true" />}

      <aside
        id="app-sidebar"
        ref={sidebarRef}
        className="app-sidebar"
        data-open={open ? 'true' : 'false'}
      >
        <div className="sidebar-brand">
          <BrandMark to={homePath} />
        </div>

        {/* One <nav>, named. A screen reader lists landmarks, and an unnamed
            navigation is indistinguishable from any other. */}
        <nav aria-label="Main" className="sidebar-nav" onClick={close}>
          {sections.map((section) => (
            <div className="nav-section" key={section.label}>
              <p className="nav-section-label">{section.label}</p>
              {section.items.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === '/admin'}
                  className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                >
                  <NavIcon name={item.icon} />
                  <span>{item.label}</span>
                  {(badges?.[item.path] ?? 0) > 0 && (
                    <span className="nav-badge" data-testid={`nav-badge-${item.path}`}>
                      <span className="sr-only">, </span>
                      {badges![item.path]}
                      <span className="sr-only"> unread</span>
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="sidebar-foot">
          {/* Beside the person, not in a settings page. A theme is not a
              setting somebody goes looking for — it is a thing they want
              changed NOW, usually because the room they are in changed — and
              the foot of the sidebar is where the other two facts about this
              session (who you are, how to leave) already live. */}
          <ThemeToggle />

          <div className="user-row">
            <span className="user-avatar" aria-hidden="true">
              {initials(user.name || user.email)}
            </span>
            <span className="user-meta">
              <span className="user-name" title={user.email}>
                {user.name || user.email}
              </span>
              <span className="user-role">{user.role}</span>
            </span>
          </div>
          <button type="button" className="btn btn-ghost small sign-out" onClick={onSignOut}>
            <NavIcon name="signout" size={16} />
            Sign out
          </button>
          {/* The two documents, where the footer would be if the shell had one:
              a creator asking "what did I agree to" should not have to sign
              out to find it. */}
          <p className="sidebar-legal">
            <Link to="/terms">Terms</Link>
            <Link to="/privacy">Privacy</Link>
          </p>
        </div>
      </aside>

      <div className="app-main">
        <header className="app-topbar">
          <button
            ref={toggleRef}
            type="button"
            className="sidebar-toggle"
            aria-expanded={open}
            aria-controls="app-sidebar"
            aria-label={open ? 'Close navigation' : 'Open navigation'}
            onClick={() => setOpen((v) => !v)}
          >
            <NavIcon name={open ? 'close' : 'menu'} size={20} />
          </button>
          <p className="topbar-title">{title}</p>
        </header>

        <main className="shell-main" id="main" tabIndex={-1}>
          <PageTitleProvider publish={setPageTitle}>{children}</PageTitleProvider>
        </main>
      </div>
    </div>
  );
}

/** Two letters for the avatar, from whatever name there is. */
function initials(from: string): string {
  const letters = from.replace(/[^a-zA-Z0-9]/g, '').slice(0, 2);
  return letters ? letters.toUpperCase() : '?';
}
